// WHY A NEW FILE: src/hooks/useOrders/models.ts opens with a file-wide
// checker-off pragma that would silently disable the `satisfies`
// exhaustiveness gate below.
// This file is fully type-checked. Do NOT move these functions into models.ts.
//
// Every BE key name below is derived from the legacy deparsers on the same
// fixture shapes — Order.deparseUpdate and Order.deparse, both in models.ts.
// Each field deparser below mirrors the corresponding legacy emission
// exactly. Never invent a BE key name that the legacy deparsers did not
// already send.
import type {
  Contact_Create,
  Order_Create,
  Order_Update,
  OrderMaterialTotal_Create,
  OrderMaterialTotal_Update,
  Waypoint_Create,
  Waypoint_Update_Destroyable,
} from '@treadinc/horizon-api-spec';
import {
  OrderUnitOfMeasure,
  RateType,
  UnitOfDistance,
  WaypointType,
} from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { isNil } from 'lodash-es';

import { ContactTypes } from '~constants/enums';
import { Phase } from '~hooks/useProjects/models';
import {
  deparseRateDetailsDTO,
  deparseUpdateRateDetailsDTO,
} from '~pages/Sales/Orders/NewOrderForm/schema';
import type { NewOrderFormSchemaInterface } from '~pages/Sales/Orders/newOrderFormSchema';
import type { OrderFormV3SubmitPayload } from '~pages/Sales/Orders/orderFormV3/OrderFormV3';
import { convertToMeters, parseFiniteNumber } from '~utils/utilFunctions';

/**
 * The dirty map deparseUpdateV3 gates on. Values are `unknown`, not
 * `boolean`: react-hook-form marks object/array fields with NESTED maps
 * ({ project: { name: true } }), and only truthiness matters here — a field
 * with any dirty leaf is sent whole, exactly once.
 */
export type OrderDirtyFields = Partial<
  Record<keyof NewOrderFormSchemaInterface, unknown>
> & {
  // `phases` is a runtime-extra form value (not a yup schema key), so it sits
  // outside orderFieldDeparsers' `satisfies` gate. The embedded ProjectDetailsV2
  // phase drawer edits it; usePhasesDirtySync marks it dirty so deparseUpdateV3
  // emits it on update, mirroring the legacy deparser's unconditional send of
  // this field.
  phases?: unknown;
};

type OrderFieldDeparser = (
  formData: OrderFormV3SubmitPayload,
  distanceUnit?: UnitOfDistance,
) => Partial<Order_Update>;

type OrderWaypointValue = OrderFormV3SubmitPayload['pickUpWayPoint'];
type OrderContactValue = OrderFormV3SubmitPayload['allContacts'][number];

const RATE_TYPE_VALUES: readonly RateType[] = Object.values(RateType);
const ORDER_UOM_VALUES: readonly OrderUnitOfMeasure[] = Object.values(OrderUnitOfMeasure);
const WAYPOINT_TYPE_VALUES: readonly WaypointType[] = Object.values(WaypointType);

/**
 * Narrows the haulerRateType option's string `value` to the RateType enum.
 * Legacy casts with type checking disabled; v3 narrows instead (the
 * OrderFormV3.tsx toRateType precedent). A non-enum string — reachable only
 * through a malformed option — becomes null, where legacy forwarded garbage.
 */
export function toRateType(value: string | null | undefined): RateType | null {
  if (!value) {
    return null;
  }
  return RATE_TYPE_VALUES.find((rateType) => String(rateType) === value) ?? null;
}

/**
 * Narrows a form unit-of-measure `id` to the OrderUnitOfMeasure enum. Legacy
 * forwards the raw string with type checking disabled; every picker option
 * is an enum value, so the only behavior delta is degenerate
 * input: the seed's `''` placeholder becomes null instead of an empty string.
 */
export function toOrderUnitOfMeasure(
  id: string | null | undefined,
): OrderUnitOfMeasure | null {
  if (!id) {
    return null;
  }
  return ORDER_UOM_VALUES.find((unitOfMeasure) => String(unitOfMeasure) === id) ?? null;
}

type MaterialTotalFormRow = NonNullable<
  NonNullable<OrderFormV3SubmitPayload['materialTotals']>[number]
>;

// A seeded-but-untouched row (a project material with no quantity entered) must
// NOT persist — it would create a spurious zero material line on the order.
function materialTotalRowHasQuantity(row: MaterialTotalFormRow): boolean {
  return !isNil(row.totalQuantity) && String(row.totalQuantity).trim() !== '';
}

// Create body: only rows with a material AND a quantity persist.
function deparseMaterialTotalsCreate(
  rows: OrderFormV3SubmitPayload['materialTotals'],
): OrderMaterialTotal_Create[] {
  return (rows ?? [])
    .filter(
      (row): row is MaterialTotalFormRow =>
        !!row && !row.isDeleted && !!row.material?.id && materialTotalRowHasQuantity(row),
    )
    .map((row) => ({
      material_id: row.material?.id ?? '',
      total_quantity: Number(row.totalQuantity),
      unit_of_measure: toOrderUnitOfMeasure(row.unitOfMeasure?.id),
    }));
}

// Update body: a persisted row flagged isDeleted emits { id, _destroy: 1 };
// rows with a material AND quantity upsert (carrying id when persisted); blank
// new rows are dropped.
function deparseMaterialTotalsUpdate(
  rows: OrderFormV3SubmitPayload['materialTotals'],
): OrderMaterialTotal_Update[] {
  const deparsed: OrderMaterialTotal_Update[] = [];
  for (const row of rows ?? []) {
    if (!row) {
      continue;
    }
    if (row.isDeleted) {
      if (row.id) {
        deparsed.push({ id: row.id, _destroy: 1 });
      }
      continue;
    }

    const isPersistable = Boolean(row.material?.id) && materialTotalRowHasQuantity(row);

    // A row the dispatcher emptied inline — cleared its quantity or its material —
    // has to be destroyed, not omitted. Nested attributes read an absent child as
    // "leave untouched", so omitting it would accept the save and leave the old
    // value on the order. Dropping the row is only right when it was never
    // persisted: that is the seeded-but-untouched project material this feature
    // deliberately ignores.
    if (!isPersistable) {
      if (row.id) {
        deparsed.push({ id: row.id, _destroy: 1 });
      }
      continue;
    }

    deparsed.push({
      ...(row.id ? { id: row.id } : {}),
      material_id: row.material?.id ?? '',
      total_quantity: Number(row.totalQuantity),
      unit_of_measure: toOrderUnitOfMeasure(row.unitOfMeasure?.id),
    });
  }
  return deparsed;
}

/**
 * Narrows a waypoint form value's `type` string to the WaypointType enum.
 * Waypoint slots are type-fixed by construction (the seed and
 * createWaypointFormValue both stamp the slot's enum value), so the fallback
 * only covers the degenerate `''` seed placeholder — the slot's own type,
 * which is what the value always is in practice.
 */
function toWaypointType(value: string | undefined, slotType: WaypointType): WaypointType {
  if (!value) {
    return slotType;
  }
  return (
    WAYPOINT_TYPE_VALUES.find((waypointType) => String(waypointType) === value) ??
    slotType
  );
}

/**
 * Mirrors ContactItem.deparse over the waypoint's plain {name, phone}
 * contact: empty members become undefined and the phone is stripped to
 * digits.
 *
 * A PERSISTED waypoint emits the explicit `{ name: '', phone: '' }` pair
 * instead of omitting an all-empty contact: the BE reads an absent `contact`
 * key as "leave it alone" and clears only on that pair, so omitting it (what
 * WayPoint.deparse does) makes "Remove Site Contact" a silent no-op. Unsaved
 * slots keep the omission. projectFormV2's buildWaypointsForUpdate matches.
 */
function deparseWaypointContact(
  contact: NonNullable<OrderWaypointValue>['contact'],
  waypointIsPersisted: boolean,
): Contact_Create | undefined {
  const name = contact?.name ?? '';
  const phone = contact?.phone ?? '';
  if (!name.length && !phone.length) {
    return waypointIsPersisted ? { name: '', email: undefined, phone: '' } : undefined;
  }
  return {
    name: name.length ? name : undefined,
    email: undefined,
    phone: phone.length ? phone.replace(/[^+\d]/g, '') : undefined,
  };
}

/**
 * Typed twin of WayPoint.deparse for the order form's waypoint values.
 * Legacy reaches it through two casts (`as WayPoint`, `as WayPointFormProps`)
 * and gets `any` back; v3 may do neither, so the literal is rebuilt 1:1
 * against the spec types. `cleanupIds` mirrors the create path's id
 * stripping.
 */
function deparseOrderWaypoint(
  waypoint: NonNullable<OrderWaypointValue>,
  slotType: WaypointType,
  slotOrdinality: number,
  cleanupIds: boolean,
): Waypoint_Create {
  // The `'undefined'` string guard is legacy-verbatim.
  const persistedId =
    !cleanupIds && waypoint.id && waypoint.id !== 'undefined' ? waypoint.id : null;

  return {
    type: toWaypointType(waypoint.type, slotType),
    ...(persistedId ? { id: persistedId } : {}),
    // Slot ordinality fallbacks are the seed's own.
    ordinality: waypoint.ordinality ?? slotOrdinality,
    contact: deparseWaypointContact(waypoint.contact, Boolean(persistedId)),
    site_id: waypoint.site?.id || '',
  };
}

/**
 * The update-body waypoints array: a site-bearing slot is deparsed in place;
 * a site-less slot that still carries a persisted waypoint id emits a
 * `_destroy` marker; an empty slot contributes nothing. Any dirty waypoint
 * field emits the COMPLETE array so the cross-slot destroy semantics stay
 * intact — slot order is pickup, dropoff, staging like legacy.
 */
function deparseUpdateWaypoints(
  formData: OrderFormV3SubmitPayload,
): Array<Waypoint_Update_Destroyable> {
  const pickUpWaypointId = formData.pickUpWayPoint?.id;
  const dropOffWaypointId = formData.dropOffWayPoint?.id;
  const stagingWaypointId = formData.stagingWayPoint?.id;

  const pickUpWayPoint = formData.pickUpWayPoint?.site?.id
    ? deparseOrderWaypoint(formData.pickUpWayPoint, WaypointType.PICKUP, 10, false)
    : null;
  const dropOffWayPoint = formData.dropOffWayPoint?.site?.id
    ? deparseOrderWaypoint(formData.dropOffWayPoint, WaypointType.DROP_OFF, 10, false)
    : null;
  const stagingWayPoint = formData.stagingWayPoint?.site?.id
    ? deparseOrderWaypoint(formData.stagingWayPoint, WaypointType.STAGING, 15, false)
    : null;

  return [
    ...(pickUpWayPoint
      ? [pickUpWayPoint]
      : pickUpWaypointId
        ? [{ id: pickUpWaypointId, _destroy: 1 }]
        : []),
    ...(dropOffWayPoint
      ? [dropOffWayPoint]
      : dropOffWaypointId
        ? [{ id: dropOffWaypointId, _destroy: 1 }]
        : []),
    ...(stagingWayPoint
      ? [stagingWayPoint]
      : stagingWaypointId
        ? [{ id: stagingWaypointId, _destroy: 1 }]
        : []),
  ];
}

/** The create-body waypoints array: ids stripped, empty slots dropped. */
function deparseCreateWaypoints(
  formData: OrderFormV3SubmitPayload,
): Array<Waypoint_Create> {
  const pickUpWayPoint = formData.pickUpWayPoint?.site?.id
    ? deparseOrderWaypoint(formData.pickUpWayPoint, WaypointType.PICKUP, 10, true)
    : null;
  const dropOffWayPoint = formData.dropOffWayPoint?.site?.id
    ? deparseOrderWaypoint(formData.dropOffWayPoint, WaypointType.DROP_OFF, 10, true)
    : null;
  const stagingWayPoint = formData.stagingWayPoint?.site?.id
    ? deparseOrderWaypoint(formData.stagingWayPoint, WaypointType.STAGING, 15, true)
    : null;
  return [pickUpWayPoint, dropOffWayPoint, stagingWayPoint].filter(
    (waypoint): waypoint is Waypoint_Create => waypoint !== null,
  );
}

/**
 * The full legacy contact pipeline for one contact type, fused and typed:
 * filterContact (first allContacts row of the type) → transformContact
 * (deleted rows: omitted on create, `_destroy` on update; live rows:
 * ContactItem.deparse normalization) → the ExtendedContactItem.deparseUpdate
 * wrap legacy applies on BOTH paths, which pads the object to the full
 * five-key shape.
 */
function deparseOrderContact(
  allContacts: OrderFormV3SubmitPayload['allContacts'],
  contactType: ContactTypes,
  isUpdate: boolean,
): (Contact_Create & { id?: string; _destroy?: number }) | undefined {
  const contact: OrderContactValue | null =
    allContacts?.filter((entry) => entry?.type?.id === contactType)?.find(Boolean) ||
    null;

  if (!contact || (contact.isDeleted && !isUpdate)) {
    return undefined;
  }
  if (contact.isDeleted) {
    return {
      id: contact.id,
      name: undefined,
      email: undefined,
      phone: undefined,
      _destroy: 1,
    };
  }
  return {
    id: undefined,
    name: contact.name?.length ? contact.name : undefined,
    email: undefined,
    phone: contact.phone?.length ? contact.phone.replace(/[^+\d]/g, '') : undefined,
    _destroy: undefined,
  };
}

/**
 * The form's `phase` is a plain {id, name, code} (the Phase class never
 * reaches the form value), so Phase.deparse is rebuilt inline; the form
 * shape has no `primary`, making legacy's `phase?.primary || false` a
 * constant. Both legacy bodies use the CREATE shape for `phase` — even
 * deparseUpdate.
 */
function deparsePhaseFormValue(
  phase: OrderFormV3SubmitPayload['phase'],
): { name: string; code: string | undefined; primary: boolean } | undefined {
  if (!phase?.name?.length) {
    return undefined;
  }
  return {
    name: phase.name || '',
    code: phase.code || undefined,
    primary: false,
  };
}

/**
 * Rate triplets for the update body: when all three of a target's form
 * fields are absent (the form omitted them because the user did not
 * explicitly modify the rate), the helper receives undefined and emits
 * nothing, so cascade-written Site Rate Schedule values are never clobbered
 * with nulls.
 *
 * Legacy wraps the WHOLE RateDetailsDTO extract as the helper's `targetRate`
 * (the DTO has no `.id`), so the helper's rate-card branch is unreachable
 * and `*_rate_id` is always null for a modified rate. v3 reproduces that
 * byte-for-byte by passing `targetRate: null`.
 */
function deparseUpdateOrderRates(
  formData: OrderFormV3SubmitPayload,
): Partial<Order_Update> {
  const customerRate = deparseUpdateRateDetailsDTO(
    'customer',
    formData.customerRate === undefined &&
      formData.customerRateType === undefined &&
      formData.customerRateValue === undefined
      ? undefined
      : {
          targetRate: null,
          targetRateType: formData.customerRateType ?? null,
          targetRateValue: formData.customerRateValue ?? null,
        },
  );
  const vendorRate = deparseUpdateRateDetailsDTO(
    'vendor',
    formData.vendorRate === undefined &&
      formData.vendorRateType === undefined &&
      formData.vendorRateValue === undefined
      ? undefined
      : {
          targetRate: null,
          targetRateType: formData.vendorRateType ?? null,
          targetRateValue: formData.vendorRateValue ?? null,
        },
  );
  const driverRate = deparseUpdateRateDetailsDTO(
    'driver',
    formData.driverRate === undefined &&
      formData.driverRateType === undefined &&
      formData.driverRateValue === undefined
      ? undefined
      : {
          targetRate: null,
          targetRateType: formData.driverRateType ?? null,
          targetRateValue: formData.driverRateValue ?? null,
        },
  );
  return { ...customerRate, ...vendorRate, ...driverRate };
}

/** Rate triplets for the create body — same wrap, create helper. */
function deparseCreateOrderRates(
  formData: OrderFormV3SubmitPayload,
): Partial<Order_Create> {
  const customerRate = deparseRateDetailsDTO(
    'customer',
    formData.customerRate === undefined &&
      formData.customerRateType === undefined &&
      formData.customerRateValue === undefined
      ? undefined
      : {
          targetRate: null,
          targetRateType: formData.customerRateType ?? null,
          targetRateValue: formData.customerRateValue ?? null,
        },
  );
  const vendorRate = deparseRateDetailsDTO(
    'vendor',
    formData.vendorRate === undefined &&
      formData.vendorRateType === undefined &&
      formData.vendorRateValue === undefined
      ? undefined
      : {
          targetRate: null,
          targetRateType: formData.vendorRateType ?? null,
          targetRateValue: formData.vendorRateValue ?? null,
        },
  );
  const driverRate = deparseRateDetailsDTO(
    'driver',
    formData.driverRate === undefined &&
      formData.driverRateType === undefined &&
      formData.driverRateValue === undefined
      ? undefined
      : {
          targetRate: null,
          targetRateType: formData.driverRateType ?? null,
          targetRateValue: formData.driverRateValue ?? null,
        },
  );
  return { ...customerRate, ...vendorRate, ...driverRate };
}

/**
 * Per-field deparser map for the UPDATE body. The `satisfies` clause is the
 * exhaustiveness gate: a NewOrderFormSchemaInterface key without an
 * entry here is a compile error, so a new schema field cannot silently skip
 * the save path. Keys that legacy's deparseUpdate never reads map to `{}` —
 * each one says where the data actually travels.
 *
 * Key order mirrors fieldMetadata.ts (the schema interface's own order).
 */
const orderFieldDeparsers = {
  // The order id rides the request PATH, never the body.
  id: () => ({}),
  // Order_Update has no company_id — the owning company is immutable
  // (legacy's update body never emits it either; create does).
  company: () => ({}),
  jobStartAt: (f) => ({
    job_start_at: f.jobStartAt ? dayjs(f.jobStartAt).toISOString() : undefined,
  }),
  loadAt: (f) => ({
    load_at: f.loadAt ? dayjs(f.loadAt).toISOString() : undefined,
  }),
  // Display-only order number; never deparsed.
  orderId: () => ({}),
  // The schema's `notes` mirror is seed-only — the BE `notes` key is fed by
  // orderNotes instead, matching the legacy behavior.
  notes: () => ({}),
  jobNotes: (f) => ({
    job_notes: f.jobNotes?.length ? f.jobNotes : '',
  }),
  // Read-only mirror of the selected project's notes; never deparsed.
  projectNotes: () => ({}),
  orderNotes: (f) => ({
    // The BE key is `notes`.
    notes: f.orderNotes?.length ? f.orderNotes : '',
  }),
  internalNotes: (f) => ({
    internal_notes: f.internalNotes?.length ? f.internalNotes : '',
  }),
  jobTime: (f) => ({
    // Hours in the form, minutes on the wire.
    job_time:
      isNil(f.jobTime) || `${f.jobTime}`.length === 0 ? null : Number(f.jobTime) * 60,
  }),
  truckCount: (f) => ({
    truck_count: Number(f.truckCount),
  }),
  truckDelay: (f) => ({
    truck_delay: Number(f.truckDelay || 0),
  }),
  trucksPerDelay: (f) => ({
    trucks_per_delay: Number(f.trucksPerDelay || 1),
  }),
  loadsPerTruck: () => ({
    // Legacy unconditionally overwrites the key with undefined on update;
    // the BE derives loads_per_truck itself.
    loads_per_truck: undefined,
  }),
  unitOfMeasure: (f) => ({
    unit_of_measure: toOrderUnitOfMeasure(f.unitOfMeasure?.id),
  }),
  // Order_Update has no account_id — the account follows the project on the
  // BE (legacy's update body never emits it either; create does).
  account: () => ({}),
  project: (f) => ({
    // Deliberate divergence from legacy, which OMITS the key when no project
    // is set: a dirty-but-empty project is the user clearing it, and the BE
    // detaches only on an explicit null. Legacy's omission is why its own
    // clear-project action never persisted.
    project_id: f.project?.id?.length ? f.project.id : null,
  }),
  material: (f) => ({
    material_id: f.material?.id ?? null,
  }),
  equipmentType: (f) => ({
    equipment_type_id: f.equipmentType?.id,
  }),
  service: (f) => ({
    service_id: f.service?.id || '',
  }),
  quantity: (f) => ({
    // Falsy → null; Number-normalized for the typed tie (legacy forwards the
    // raw form string unchecked).
    quantity: f.quantity ? Number(f.quantity) || null : null,
  }),
  // Order state transitions travel through dedicated endpoints, not the form.
  state: () => ({}),
  // Any dirty waypoint slot emits the COMPLETE legacy-shaped array (see
  // deparseUpdateWaypoints) — Object.assign overwrites with an identical
  // value when several slots are dirty at once.
  pickUpWayPoint: (f) => ({ waypoints: deparseUpdateWaypoints(f) }),
  dropOffWayPoint: (f) => ({ waypoints: deparseUpdateWaypoints(f) }),
  stagingWayPoint: (f) => ({ waypoints: deparseUpdateWaypoints(f) }),
  phase: (f) => ({
    // Uses the create shape, even on update.
    phase: deparsePhaseFormValue(f.phase),
  }),
  // phaseName/phaseCode are UI projections of `phase`; never deparsed.
  phaseName: () => ({}),
  phaseCode: () => ({}),
  foremen: (f) => ({
    foreman_ids: f.foremen?.map((foreman) => foreman.id) || [],
  }),
  collaborators: (f) => ({
    collaborator_ids: f.collaborators?.map((collaborator) => collaborator.id) || [],
  }),
  salespeople: (f) => ({
    salesperson_ids: f.salespeople?.map((salesperson) => salesperson.id) || [],
  }),
  allContacts: (f) => ({
    // The four contact slots travel together.
    sales_contact: deparseOrderContact(f.allContacts, ContactTypes.SALES, true),
    foreman_contact: deparseOrderContact(f.allContacts, ContactTypes.FOREMAN, true),
    collaborator_contact: deparseOrderContact(
      f.allContacts,
      ContactTypes.COLLABORATOR,
      true,
    ),
    supervisor_contact: deparseOrderContact(f.allContacts, ContactTypes.SUPERVISOR, true),
  }),
  // Read-only project material totals / phase list; never deparsed.
  projectMaterialTotals: () => ({}),
  materialTotals: (f) => {
    const materialTotals = deparseMaterialTotalsUpdate(f.materialTotals);
    // Omit the key entirely when there's nothing to upsert or destroy: nested
    // attributes read an absent child as "leave untouched".
    return materialTotals.length ? { material_totals: materialTotals } : {};
  },
  allPhaseCodes: () => ({}),
  department: (f) => ({
    department_id: f.department?.id || null,
  }),
  name: (f) => ({
    name: f.name || '',
  }),
  poJobNumber: (f) => ({
    po_job_number: f.poJobNumber || '',
  }),
  zone: (f) => ({
    zone: f.zone || '',
  }),
  externalId: (f) => ({
    external_id: f.externalId || '',
  }),
  serviceClass: (f) => ({
    service_class_id: f.serviceClass?.id || null,
  }),
  equipmentTypeGrossCapacityUnitOfMeasure: (f) => ({
    equipment_type_gross_capacity_unit_of_measure: toOrderUnitOfMeasure(
      f.equipmentTypeGrossCapacityUnitOfMeasure?.id,
    ),
  }),
  equipmentTypeGrossCapacity: (f) => ({
    equipment_type_gross_capacity: parseFiniteNumber(f.equipmentTypeGrossCapacity),
  }),
  loadUnitOfMeasure: (f) => ({
    load_unit_of_measure: toOrderUnitOfMeasure(f.loadUnitOfMeasure?.id),
  }),
  unitsPerHour: (f) => ({
    // Falsy → null; Number-normalized for the typed tie.
    units_per_hour: f.unitsPerHour ? Number(f.unitsPerHour) : null,
  }),
  jobUnitOfMeasure: (f) => ({
    job_unit_of_measure: toOrderUnitOfMeasure(f.jobUnitOfMeasure?.id),
  }),
  jobQuantity: (f) => {
    // Standing Haul orders zero-fill a missing job quantity.
    // serviceIsStandingHaul takes the Service class; the form holds the
    // schema shape, so the name compare is inlined. The typed tie sends null
    // where legacy forwarded a raw '' — the BE casts both to nil.
    const isStandingHaulService = f.service?.name === 'Standing Haul';
    const value = f.jobQuantity;
    return {
      job_quantity: isNil(value)
        ? isStandingHaulService
          ? 0
          : null
        : String(value).trim() === ''
          ? null
          : Number(value),
    };
  },
  contractNumber: (f) => ({
    contract_number: f.contractNumber || null,
  }),
  dispatchNumber: (f) => ({
    // Clearing a persisted dispatch number destroys it.
    dispatch_number:
      f.dispatchNumber && f.dispatchNumber.trim() !== ''
        ? { name: f.dispatchNumber }
        : f.dispatchNumber === '' && f.id
          ? { _destroy: 1 }
          : undefined,
  }),
  // File attachments travel through the fileAttachmentsOnSubmit handler
  // lifecycle, never the order body.
  fileAttachments: () => ({}),
  requester: (f) => ({
    requester_id: f.requester?.id || null,
  }),
  // Display-only estimate; never deparsed.
  estimatedDeliveredQuantity: () => ({}),
  cycleTime: (f) => ({
    cycle_time_minutes: parseFiniteNumber(f.cycleTime),
  }),
  cycleDistance: (f, distanceUnit) => {
    // The only consumer of the distanceUnit argument.
    const parsedCycleDistance = parseFiniteNumber(f.cycleDistance);
    return {
      pickup_to_dropoff_distance_meters:
        parsedCycleDistance === null
          ? null
          : distanceUnit
            ? convertToMeters(parsedCycleDistance, distanceUnit)
            : parsedCycleDistance,
    };
  },
  pickupOnsite: (f) => ({
    pickup_onsite_minutes: parseFiniteNumber(f.pickupOnsite),
  }),
  dropoffOnsite: (f) => ({
    dropoff_onsite_minutes: parseFiniteNumber(f.dropoffOnsite),
  }),
  pickupToDropoff: (f) => ({
    pickup_to_dropoff_time_minutes: parseFiniteNumber(f.pickupToDropoff),
  }),
  totalLoads: (f) => ({
    expected_total_loads: parseFiniteNumber(f.totalLoads),
  }),
  recurringLoads: (f) => ({
    recurring_loads: Boolean(f.recurringLoads),
  }),
  chaseMissingTickets: (f) => ({
    chase_missing_tickets: Boolean(f.chaseMissingTickets),
  }),
  liveEta: (f) => ({
    live_eta: Boolean(f.liveEta),
  }),
  movingSiteSyncEnabled: (f) => ({
    moving_site_sync_enabled: Boolean(f.movingSiteSyncEnabled),
  }),
  defaultDigitalTicketView: (f) => ({
    default_digital_ticket_view: Boolean(f.defaultDigitalTicketView),
  }),
  jobsSplittable: (f) => ({
    jobs_splittable: Boolean(f.jobsSplittable),
  }),
  prevailingWage: (f) => ({
    prevailing_wage: Boolean(f.prevailingWage),
  }),
  taxRateSchedule: (f) => ({
    tax_rate_schedule_id: f.taxRateSchedule?.value ?? null,
  }),
  taxRate: (f) => ({
    tax_rate: isNil(f.taxRate) ? null : Number(f.taxRate),
  }),
  taxFreight: (f) => ({
    tax_freight: f.taxFreight ?? false,
  }),
  taxMaterial: (f) => ({
    tax_material: f.taxMaterial ?? false,
  }),
  taxAddOns: (f) => ({
    tax_add_ons: f.taxAddOns ?? false,
  }),
  haulerRateType: (f) => ({
    hauler_rate_type: toRateType(f.haulerRateType?.value),
  }),
  materialRate: (f) => ({
    // Update never sends material_rate_site_id (create does).
    material_rate_id: f.materialRate?.id ?? null,
  }),
  materialRateUoM: (f) => ({
    material_rate_unit_of_measure: toOrderUnitOfMeasure(f.materialRateUoM?.id),
  }),
  materialRateBuyPrice: (f) => ({
    material_rate_buy_price: parseFiniteNumber(f.materialRateBuyPrice),
  }),
  materialRateSellPrice: (f) => ({
    material_rate_sell_price: parseFiniteNumber(f.materialRateSellPrice),
  }),
  vendorPayCommissionPercentage: (f) => ({
    vendor_pay_commission_percentage: f.vendorPayCommissionPercentage,
  }),
  driverPayCommissionPercentage: (f) => ({
    driver_pay_commission_percentage: f.driverPayCommissionPercentage,
  }),
  materialRateItemized: (f) => ({
    material_rate_itemized: Boolean(f.materialRateItemized),
  }),
  // Add-on assignments travel through the rates/SRS pipeline side-channels,
  // never the order body.
  vendorAddOnAssignments: () => ({}),
  customerAddOnAssignments: () => ({}),
  driverAddOnAssignments: () => ({}),
  geofenceDelaySeconds: (f) => ({
    // Legacy emits `?? null`, but Order_Update types the key number-only —
    // undefined drops the key instead, so a cleared geofence keeps its
    // persisted value rather than clearing it (spec-tie delta; Order_Create
    // still takes the null).
    geofence_delay_seconds: f.geofenceDelaySeconds ?? undefined,
  }),
  chargeToAccount: (f) => ({
    charge_to_account: f.chargeToAccount ?? null,
  }),
  defaultSiteRateScheduleId: (f) => ({
    // Legacy sends it unconditionally; v3 gates on the dirty map like every
    // other schema key, so the caller must mark it dirty when FreightRates'
    // onDefaultSiteRateScheduleChange fires (the setter lives outside RHF).
    default_site_rate_schedule_id: f.defaultSiteRateScheduleId ?? null,
  }),
} as const satisfies Record<keyof NewOrderFormSchemaInterface, OrderFieldDeparser>;

/**
 * Sparse PATCH body: only fields marked dirty are deparsed — an untouched
 * form yields `{}`, and OrderForm.tsx skips the API call. The rate triplets
 * are imperative side-channels with no dirtyFields entry; their own
 * all-undefined guard decides emission, mirroring the legacy deparser's
 * unconditional spreads.
 */
export function deparseUpdateV3(
  formData: OrderFormV3SubmitPayload,
  dirtyFields: OrderDirtyFields,
  distanceUnit?: UnitOfDistance,
): Partial<Order_Update> {
  const result: Partial<Order_Update> = {};
  (Object.keys(orderFieldDeparsers) as Array<keyof NewOrderFormSchemaInterface>).forEach(
    (key) => {
      if (dirtyFields[key]) {
        Object.assign(result, orderFieldDeparsers[key](formData, distanceUnit));
      }
    },
  );
  // `phases` is the one edited runtime-extra outside the schema-key set above:
  // emit the full legacy-shaped array (incl. _destroy markers) when the drawer
  // dirtied it. usePhasesDirtySync is the caller that marks it dirty; an
  // untouched form leaves it absent so the PATCH stays sparse.
  if (dirtyFields.phases) {
    result.phases = formData.phases?.length
      ? formData.phases.map((phase) => Phase.deparseUpdate(phase))
      : [];
  }
  Object.assign(result, deparseUpdateOrderRates(formData));
  return result;
}

/**
 * Full create body, typed Order_Create (not Partial) — a missing required
 * field is a compile error, which is the BE-tie guarantee. Key set and
 * values mirror legacy Order.deparse 1:1 on the same payload, including the
 * keys legacy assigns `undefined` (JSON drops them; the key-set parity test
 * reads them through Object.keys like legacy's output has them).
 */
export function deparseCreateV3(
  formData: OrderFormV3SubmitPayload,
  distanceUnit?: UnitOfDistance,
): Order_Create {
  const parsedCycleDistance = parseFiniteNumber(formData.cycleDistance);
  // Omit material_totals entirely when there are no persistable rows, so a
  // single-material create sends no empty array.
  const materialTotalsCreate = deparseMaterialTotalsCreate(formData.materialTotals);

  return {
    company_id: formData.company?.id,
    project_id: formData.project?.id?.length ? formData.project.id : undefined,
    equipment_type_id: formData.equipmentType?.id,
    material_id: formData.material?.id,
    // The order's additive material list; blank/seeded rows are dropped, and the
    // key is omitted entirely when empty, matching the single-material body.
    ...(materialTotalsCreate.length ? { material_totals: materialTotalsCreate } : {}),
    // Falsy → undefined; Number-normalized for the typed tie.
    quantity: formData.quantity ? Number(formData.quantity) || undefined : undefined,
    unit_of_measure: toOrderUnitOfMeasure(formData.unitOfMeasure?.id),
    job_start_at: formData.jobStartAt
      ? dayjs(formData.jobStartAt).toISOString()
      : undefined,
    // Order_Create requires the key; the resolver guarantees a value, and
    // the '' fallback keeps an (unreachable) empty seed from throwing inside
    // dayjs.
    load_at: formData.loadAt ? dayjs(formData.loadAt).toISOString() : '',
    notes: formData.orderNotes?.length ? formData.orderNotes : undefined,
    job_notes: formData.jobNotes?.length ? formData.jobNotes : undefined,
    internal_notes: formData.internalNotes?.length ? formData.internalNotes : undefined,
    truck_count: Number(formData.truckCount || 0),
    truck_delay: Number(formData.truckDelay || 0),
    trucks_per_delay: Number(formData.trucksPerDelay || 1),
    // Legacy computes a value here, then unconditionally overwrites it with
    // undefined — the final body never carries one.
    loads_per_truck: undefined,
    account_id: formData.account?.id || '',
    service_id: formData.service?.id || '',
    waypoints: deparseCreateWaypoints(formData),
    phase: deparsePhaseFormValue(formData.phase),
    // The seed-only `phases` field (copy-and-edit carries the source
    // order's phases through it).
    phases: formData.phases?.length
      ? formData.phases.map((phase) => Phase.deparse(phase))
      : [],
    // Rate fields are conditional spreads.
    ...deparseCreateOrderRates(formData),
    sales_contact: deparseOrderContact(formData.allContacts, ContactTypes.SALES, false),
    foreman_contact: deparseOrderContact(
      formData.allContacts,
      ContactTypes.FOREMAN,
      false,
    ),
    collaborator_contact: deparseOrderContact(
      formData.allContacts,
      ContactTypes.COLLABORATOR,
      false,
    ),
    supervisor_contact: deparseOrderContact(
      formData.allContacts,
      ContactTypes.SUPERVISOR,
      false,
    ),
    department_id: formData.department?.id || null,
    service_class_id: formData.serviceClass?.id || null,
    foreman_ids: formData.foremen?.map((foreman) => foreman.id) || [],
    collaborator_ids:
      formData.collaborators?.map((collaborator) => collaborator.id) || [],
    name: formData.name || '',
    po_job_number: formData.poJobNumber || '',
    zone: formData.zone || '',
    external_id: formData.externalId || '',
    units_per_hour: formData.unitsPerHour ? Number(formData.unitsPerHour) : null,
    job_quantity: formData.jobQuantity ? Number(formData.jobQuantity) : null,
    job_unit_of_measure: toOrderUnitOfMeasure(formData.jobUnitOfMeasure?.id),
    // No destroy marker on create.
    dispatch_number: formData.dispatchNumber
      ? { name: formData.dispatchNumber }
      : undefined,
    contract_number: formData.contractNumber || null,
    requester_id: formData.requester?.id || null,
    hauler_rate_type: toRateType(formData.haulerRateType?.value),
    material_rate_id: formData.materialRate?.id ?? null,
    material_rate_site_id: formData.materialRate?.site?.id ?? null,
    material_rate_unit_of_measure: toOrderUnitOfMeasure(formData.materialRateUoM?.id),
    material_rate_sell_price: parseFiniteNumber(formData.materialRateSellPrice),
    material_rate_buy_price: parseFiniteNumber(formData.materialRateBuyPrice),
    geofence_delay_seconds: formData.geofenceDelaySeconds ?? null,
    load_unit_of_measure: toOrderUnitOfMeasure(formData.loadUnitOfMeasure?.id),
    charge_to_account: formData.chargeToAccount ?? null,
    salesperson_ids: formData.salespeople?.map((salesperson) => salesperson.id) || [],
    job_time:
      isNil(formData.jobTime) || `${formData.jobTime}`.length === 0
        ? null
        : Number(formData.jobTime) * 60,
    cycle_time_minutes: parseFiniteNumber(formData.cycleTime),
    pickup_onsite_minutes: parseFiniteNumber(formData.pickupOnsite),
    dropoff_onsite_minutes: parseFiniteNumber(formData.dropoffOnsite),
    pickup_to_dropoff_time_minutes: parseFiniteNumber(formData.pickupToDropoff),
    pickup_to_dropoff_distance_meters:
      parsedCycleDistance === null
        ? null
        : distanceUnit
          ? convertToMeters(parsedCycleDistance, distanceUnit)
          : parsedCycleDistance,
    expected_total_loads: parseFiniteNumber(formData.totalLoads),
    equipment_type_gross_capacity: parseFiniteNumber(formData.equipmentTypeGrossCapacity),
    equipment_type_gross_capacity_unit_of_measure: toOrderUnitOfMeasure(
      formData.equipmentTypeGrossCapacityUnitOfMeasure?.id,
    ),
    recurring_loads: Boolean(formData.recurringLoads),
    material_rate_itemized: Boolean(formData.materialRateItemized),
    vendor_pay_commission_percentage: formData.vendorPayCommissionPercentage,
    driver_pay_commission_percentage: formData.driverPayCommissionPercentage,
    chase_missing_tickets: Boolean(formData.chaseMissingTickets),
    live_eta: Boolean(formData.liveEta),
    moving_site_sync_enabled: Boolean(formData.movingSiteSyncEnabled),
    default_digital_ticket_view: Boolean(formData.defaultDigitalTicketView),
    jobs_splittable: Boolean(formData.jobsSplittable),
    prevailing_wage: Boolean(formData.prevailingWage),
    tax_rate: isNil(formData.taxRate) ? null : Number(formData.taxRate),
    tax_freight: formData.taxFreight ?? false,
    tax_material: formData.taxMaterial ?? false,
    tax_add_ons: formData.taxAddOns ?? false,
    tax_rate_schedule_id: formData.taxRateSchedule?.value ?? null,
    default_site_rate_schedule_id: formData.defaultSiteRateScheduleId ?? null,
  };
}
